import { createEffect, createEvent, createStore, sample } from 'effector';
import { OrgServiceOrgUsageDto } from '@internal/org-service';
import { axiosClient } from '@nx-frontend/shared/api';
import { generalEnv } from '@nx-frontend/shared/env';
import { Org, OrgUsage } from '@nx-frontend/shared/types';

export type OrgUsageState = {
  isFetching: boolean;
  isLoaded: boolean;
  data: OrgUsage;
};

export const initialOrgUsageState: OrgUsageState = {
  isFetching: false,
  isLoaded: false,
  data: {
    blots: { current: 0, max: 0 },
    clients: { current: 0, max: 0 },
    guests: { current: 0, max: 0 },
    members: { current: 0, max: 0 },
    storage: { current: 0, max: 0 },
    traffic: { current: 0, max: 0 },
    workspaces: { current: 0, max: 0 },
  },
};

export const startOrgUsageFetchEvent = createEvent();
export const endOrgUsageFetchEvent = createEvent<OrgUsage>();
export const loadOrgUsageEvent = createEvent<Org>();
const setOrgUsageFetchingEvent = createEvent();
const loadOrgUsageFx = createEffect(async ({ orgId }: { orgId: string }) => {
  const { data } = await axiosClient.get<OrgServiceOrgUsageDto>(`${generalEnv.nextApiPath}/orgs/${orgId}/usage`);
  return data;
});

export const $orgUsage = createStore(initialOrgUsageState)
  .on(startOrgUsageFetchEvent, (state) => {
    return { ...state, isFetching: true };
  })
  .on(endOrgUsageFetchEvent, (state, usage) => {
    return { ...state, data: usage, isFetching: false, isLoaded: true };
  });

export const $orgUsageData = $orgUsage.map((state) => state.data);

$orgUsage.on(setOrgUsageFetchingEvent, (store) => {
  if (store.isFetching || store.isLoaded) {
    return store;
  }
  return Object.assign({}, store, {
    isFetching: true,
  });
});

$orgUsage.on(loadOrgUsageFx.doneData, (orgUsageStore, data) => {
  return Object.assign({}, orgUsageStore, {
    data,
    isLoaded: true,
    isFetching: false,
  });
});

$orgUsage.on(loadOrgUsageFx.failData, (orgUsageStore) => {
  return Object.assign({}, orgUsageStore, {
    isFetching: false,
  });
});

export const $isOrgUsageLoaded = $orgUsage.map((orgUsageStore) => {
  if (!orgUsageStore) {
    return false;
  }
  return orgUsageStore.isLoaded;
});

sample({
  source: $orgUsage,
  clock: loadOrgUsageEvent,
  filter(orgUsageStore, currentOrganization) {
    return !(
      orgUsageStore.isFetching ||
      orgUsageStore.isLoaded ||
      !currentOrganization ||
      !currentOrganization.globalId
    );
  },
  fn: (orgUsageStore, currentOrganization) => {
    return {
      orgId: currentOrganization.globalId,
    };
  },
  target: [loadOrgUsageFx, setOrgUsageFetchingEvent],
});
